import { useEffect, useRef, useState } from 'react';

/** What the header loses when it compresses (size M to compress). */
const HEADER_COMPRESS_DELTA = 56;

/** Scroll that must remain once compressed: above the hook's 50px compress threshold, with margin. */
const MIN_RANGE_AFTER_COMPRESS = 70;

/**
 * Whether a list page may compress its pinned header. Compressing shortens the content by the
 * header's size change; on a short list that would leave less scroll than the compress threshold,
 * `scrollTop` would clamp under it and the header would expand again, bouncing the page. So it
 * compresses only when the content leaves room for it; otherwise the header stays pinned expanded.
 *
 * It measures only while expanded (compressed content is shorter) and re-measures when the scroller
 * or its content resizes.
 *
 * @param {import('react').RefObject<HTMLElement>} scrollRef The scroll container.
 * @param {boolean} scrolled The raw scrolled state from `useStickyScroll`.
 */
export const useCompressAllowed = (scrollRef, scrolled) => {
  const [allowed, setAllowed] = useState(true);
  const compressedRef = useRef(false);

  useEffect(() => {
    compressedRef.current = scrolled && allowed;
  }, [scrolled, allowed]);

  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return undefined;

    const measure = () => {
      if (compressedRef.current) return;
      const range = scroller.scrollHeight - scroller.clientHeight;
      setAllowed(range >= HEADER_COMPRESS_DELTA + MIN_RANGE_AFTER_COMPRESS);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(scroller);
    if (scroller.firstElementChild) observer.observe(scroller.firstElementChild);
    return () => observer.disconnect();
  }, [scrollRef]);

  return allowed;
};
